import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production" ? "https://hooks-28xd.onrender.com" : "http://localhost:4000";

const toLocalDateTimeString = (date) => {
    const pad = n => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const newExercise = () => ({ name: "", sets: "", reps: "", weight: "" });

const STORAGE_KEY = "weightlifting_last";

const loadSaved = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
};

const WeightLifting = () => {
    const navigate = useNavigate();
    const saved = loadSaved();
    const [workoutName, setWorkoutName] = useState(saved.workoutName || "Weight Training");
    const [startDateTime, setStartDateTime] = useState(toLocalDateTimeString(new Date()));
    const [durationMinutes, setDurationMinutes] = useState(saved.durationMinutes || "");
    const [exercises, setExercises] = useState(saved.exercises || [newExercise()]);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
        }
    }, [navigate]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ workoutName, durationMinutes, exercises }));
    }, [workoutName, durationMinutes, exercises]);

    const getToken = async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return authgear.accessToken;
    };

    const updateExercise = (index, field, value) => {
        setExercises(prev => prev.map((ex, i) => i === index ? { ...ex, [field]: value } : ex));
    };

    const addExercise = () => setExercises(prev => [...prev, newExercise()]);

    const removeExercise = (index) => {
        if (exercises.length === 1) return;
        setExercises(prev => prev.filter((_, i) => i !== index));
    };

    const buildDescription = () => {
        return exercises
            .filter(ex => ex.name.trim())
            .map(ex => {
                const parts = [ex.name.trim()];
                if (ex.sets || ex.reps) {
                    parts.push(`${ex.sets || "?"}x${ex.reps || "?"}`);
                }
                if (ex.weight) parts.push(`@ ${ex.weight} lbs`);
                return parts.join(" ");
            })
            .join("\n");
    };

    const handleSubmit = async () => {
        if (!workoutName.trim()) { setStatus({ ok: false, msg: "Workout name is required." }); return; }
        if (!durationMinutes || isNaN(durationMinutes) || Number(durationMinutes) <= 0) {
            setStatus({ ok: false, msg: "Enter a valid duration in minutes." });
            return;
        }

        setLoading(true);
        setStatus(null);
        try {
            const token = await getToken();
            const elapsed_time = Math.round(Number(durationMinutes) * 60);
            const description = buildDescription();

            const { data } = await axios.post(
                `${base_url}/strava/create-activity`,
                {
                    name: workoutName.trim(),
                    sport_type: "WeightTraining",
                    start_date_local: startDateTime + ":00",
                    elapsed_time,
                    description
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (data.success) {
                setStatus({ ok: true, msg: `Pushed to Strava! Activity ID: ${data.activity.id}` });
                setExercises([newExercise()]);
                setDurationMinutes("");
                setWorkoutName("Weight Training");
                setStartDateTime(toLocalDateTimeString(new Date()));
            } else {
                setStatus({ ok: false, msg: data.message || "Unknown error" });
            }
        } catch (e) {
            setStatus({ ok: false, msg: e.response?.data?.message || e.message });
        } finally {
            setLoading(false);
        }
    };

    const card = {
        backgroundColor: "#1e1e3a",
        border: "1px solid #333",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "20px"
    };

    const input = {
        background: "#111",
        border: "1px solid #555",
        borderRadius: "4px",
        color: "#ddd",
        fontSize: "14px",
        padding: "8px 10px",
        boxSizing: "border-box"
    };

    const label = { fontSize: "13px", color: "#aaa", display: "block", marginBottom: "4px" };

    const btn = (color = "#00d4ff", disabled = false) => ({
        backgroundColor: disabled ? "#444" : color,
        color: "#fff",
        border: "none",
        padding: "8px 16px",
        borderRadius: "4px",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "14px",
        opacity: disabled ? 0.6 : 1
    });

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "24px", maxWidth: "700px" }}>
                <h2 style={{ color: "#00d4ff", marginBottom: "24px" }}>Log Weight Training</h2>

                {status && (
                    <div style={{
                        ...card,
                        backgroundColor: status.ok ? "#1a2a1a" : "#2a1a1a",
                        borderColor: status.ok ? "#2a4a2a" : "#4a2a2a",
                        color: status.ok ? "#8f8" : "#f88",
                        marginBottom: "16px"
                    }}>
                        {status.msg}
                    </div>
                )}

                <div style={card}>
                    <h3 style={{ color: "#00d4ff", marginTop: 0 }}>Workout Details</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                        <div style={{ gridColumn: "1 / -1" }}>
                            <label style={label}>Workout Name</label>
                            <input
                                style={{ ...input, width: "100%" }}
                                value={workoutName}
                                onChange={e => setWorkoutName(e.target.value)}
                                placeholder="Weight Training"
                            />
                        </div>
                        <div>
                            <label style={label}>Start Date & Time</label>
                            <input
                                type="datetime-local"
                                style={{ ...input, width: "100%" }}
                                value={startDateTime}
                                onChange={e => setStartDateTime(e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={label}>Duration (minutes)</label>
                            <input
                                type="number"
                                style={{ ...input, width: "100%" }}
                                value={durationMinutes}
                                onChange={e => setDurationMinutes(e.target.value)}
                                placeholder="45"
                                min="1"
                            />
                        </div>
                    </div>
                </div>

                <div style={card}>
                    <h3 style={{ color: "#00d4ff", marginTop: 0 }}>Exercises</h3>
                    {exercises.map((ex, i) => (
                        <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1.2fr auto", gap: "8px", alignItems: "end", marginBottom: "12px" }}>
                            <div>
                                {i === 0 && <label style={label}>Exercise</label>}
                                <input
                                    style={{ ...input, width: "100%" }}
                                    value={ex.name}
                                    onChange={e => updateExercise(i, "name", e.target.value)}
                                    placeholder="Bench Press"
                                />
                            </div>
                            <div>
                                {i === 0 && <label style={label}>Sets</label>}
                                <input
                                    type="number"
                                    style={{ ...input, width: "100%" }}
                                    value={ex.sets}
                                    onChange={e => updateExercise(i, "sets", e.target.value)}
                                    placeholder="3"
                                    min="1"
                                />
                            </div>
                            <div>
                                {i === 0 && <label style={label}>Reps</label>}
                                <input
                                    type="number"
                                    style={{ ...input, width: "100%" }}
                                    value={ex.reps}
                                    onChange={e => updateExercise(i, "reps", e.target.value)}
                                    placeholder="8"
                                    min="1"
                                />
                            </div>
                            <div>
                                {i === 0 && <label style={label}>Weight (lbs)</label>}
                                <input
                                    type="number"
                                    style={{ ...input, width: "100%" }}
                                    value={ex.weight}
                                    onChange={e => updateExercise(i, "weight", e.target.value)}
                                    placeholder="135"
                                    min="0"
                                />
                            </div>
                            <div style={{ paddingBottom: "1px" }}>
                                {i === 0 && <div style={{ height: "21px" }} />}
                                <button
                                    style={{ ...btn("#dc3545", exercises.length === 1), padding: "8px 10px" }}
                                    onClick={() => removeExercise(i)}
                                    disabled={exercises.length === 1}
                                    title="Remove"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    ))}
                    <button style={{ ...btn("#444"), marginTop: "4px" }} onClick={addExercise}>
                        + Add Exercise
                    </button>
                </div>

                <button
                    style={{ ...btn("#fc4c02", loading), fontSize: "16px", padding: "12px 28px" }}
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? "Pushing to Strava..." : "Push to Strava"}
                </button>
            </div>
        </div>
    );
};

export default WeightLifting;
